import { lazy, type ComponentType } from 'react';

/**
 * 2026-10-11: Seiten erst laden, wenn sie gebraucht werden.
 *
 * Seit dem MUI-Umbau (#61–#63) lud jedes Gerät die ganze Anwendung samt MUI, auch das
 * Kiosk-Tablet, das MUI überhaupt nicht benutzt, und das Handy, das es nur für eine Rückfrage
 * braucht: rund 1.010 kB statt vorher etwa 650 kB. Kiosk und Startseite bleiben im ersten Paket;
 * alles andere kommt über diese Funktion nach.
 *
 * ## Die Falle, die das Nachladen mitbringt
 *
 * `scripts/deploy-frontend.sh` löscht bei jedem Deploy die alten Dateien (`--delete`). Wer die
 * Anwendung VOR einem Deploy geöffnet hat und danach eine Seite aufruft, die noch nicht geladen
 * war, fragt eine Datei an, die es nicht mehr gibt. CloudFront antwortet darauf mit der
 * `index.html` (Status 200, SPA-Rückfall), der Import scheitert — und ohne Gegenmaßnahme bliebe
 * die Seite weiß. Mit einem einzigen Paket gab es diesen Fall nicht: Ein offener Tab lief bis
 * zum nächsten Neuladen mit dem alten Stand weiter.
 *
 * Deshalb: **Beim ersten Fehlschlag wird die Seite einmal neu geladen** — danach steht der neue
 * Stand da, und die Datei passt. Scheitert es innerhalb kurzer Zeit erneut, ist es kein alter
 * Stand, sondern etwa eine fehlende Verbindung; dann erscheint eine Meldung mit Knopf, statt in
 * einer Neulade-Schleife zu enden.
 */

const NEU_GELADEN_KEY = 'bt-nachladen-neu-geladen-um';
/** Innerhalb dieser Spanne nach einem Neuladen wird nicht noch einmal neu geladen. */
const SCHLEIFENSCHUTZ_MS = 30_000;

function kuerzlichNeuGeladen(): boolean {
  try {
    const um = Number(sessionStorage.getItem(NEU_GELADEN_KEY));
    return Number.isFinite(um) && Date.now() - um < SCHLEIFENSCHUTZ_MS;
  } catch {
    // Ohne Speicher lässt sich eine Schleife nicht ausschließen -- also nicht neu laden.
    return true;
  }
}

function merkeNeuladen(): void {
  try {
    sessionStorage.setItem(NEU_GELADEN_KEY, String(Date.now()));
  } catch {
    /* siehe kuerzlichNeuGeladen */
  }
}

/** Austauschbar nur für Tests: jsdom kann `location.reload` weder ausführen noch ersetzen. */
export const umgebung = { neuLaden: () => window.location.reload() };

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- wie Reacts eigene Signatur von `lazy`
export function lazyPage<T extends ComponentType<any>>(laden: () => Promise<{ default: T }>) {
  return lazy(() =>
    laden().catch((fehler: unknown) => {
      if (kuerzlichNeuGeladen()) throw fehler;
      merkeNeuladen();
      umgebung.neuLaden();
      // Bis das Neuladen greift, bleibt die Ladeanzeige stehen, statt kurz einen Fehler zu zeigen.
      return new Promise<{ default: T }>(() => {});
    }),
  );
}
